import axios, { AxiosPromise, AxiosResponse } from 'axios';
import { BACKEND_URL } from '@/helpers/constants';
import AuthModule from '@/store/AuthModule';
import router from '@/router';
import MetaDatatable from '@/models/MetaDatatable';
import User from '@/models/User';
import Company from '@/models/Company';
import Sponsorship from '@/models/Sponsorship';
import Customer from '@/models/Customer';
import Program from '@/models/Program';
import Lot from '@/models/Lot';
import Denounce from '@/models/Denounce';
import OptionModel from '@/models/OptionModel';
import Booking from '@/models/Booking';
import NotificationModel from '@/models/Notification';
import Invoice from '@/models/Invoice';
import Payment from '@/models/Payement';
import Subscription from '@/models/Subscription';
import ImportCSVResult from '@/models/ImportCSVResult';
import { DOCUMENT_TYPE } from '@/data/documentTypes';
import ProgramDocumentRequest from '@/models/ProgramDocumentRequest';
import PageData from '@/models/PageData';
import DenounceRequest from '@/models/DenounceRequest';
import ImportZIPResult from '@/models/ImportZIPResult';
import Offer from '@/models/Offer';
import PaymentPrescriberRequest from '@/models/PaymentPrescriberRequest';
import SubscriptionPrescriber from '@/models/SubscriptionPrescriber';
import { DOCUMENT_PRESCRIBER_TYPE } from '@/data/documentPrescriberTypes';
import PatchBookingRegistrationRequest from '@/models/PatchBookingRegistrationRequest';
import UserToken from '@/models/UserToken';

const DEV = !process.env.NODE_ENV || process.env.NODE_ENV === 'development';

const axiosInstance = axios.create({
    baseURL: BACKEND_URL + '/api',
    headers: {},
});

let backendCheck: Promise<boolean> | null = null;

/**
 * Ping "no-cors" : résout si le serveur répond (quel que soit le statut),
 * échoue seulement si le réseau / le serveur est réellement indisponible.
 */
function isBackendReachable(): Promise<boolean> {
    if (!backendCheck) {
        backendCheck = fetch(`${BACKEND_URL}/api`, { mode: 'no-cors', cache: 'no-store' })
            .then(() => true)
            .catch(() => false)
            .finally(() => {
                backendCheck = null;
            });
    }
    return backendCheck;
}

axiosInstance.interceptors.request.use(
    config => {
        const token = AuthModule.token;
        config.headers.Authorization = `Bearer ${token}`;
        return config;
    },
    error => {
        return Promise.reject(error);
    }
);


axiosInstance.interceptors.response.use(
    response => {
        return response;
    },
    async error => {
        if (!error.response) {
            if (axios.isCancel(error)) {
                return Promise.reject(error);
            }
            // Pas de réponse lisible ne veut pas dire serveur down : une erreur fatale PHP
            // (ex : mémoire) renvoie une 500 sans en-têtes CORS. On vérifie que l'API est
            // vraiment injoignable avant d'envoyer l'utilisateur sur la page maintenance.
            if (router.currentRoute.name !== 'pageMaintenance' && !(await isBackendReachable())) {
                router.push({ name: 'pageMaintenance' });
            }
            return Promise.reject(error);
        }
        if (error.response.status === 401) {
            AuthModule.logout();
            router.push({ name: 'home' });
        }
        return Promise.reject(error);
    }
);


/**
 *  Auth - Login
 *
 * @param username Nom de l'utilisateur
 * @param password Mot de passe l'utilisateur
 */
function login(
    username: string,
    password: string
): AxiosPromise<{ token: string }> {
    return axiosInstance.post(`/login-check`, {
        username,
        password,
    });
}
/**
 *  Conntected User Information
 */
function getConnectedUser(): AxiosPromise<User> {
    return axiosInstance.get('/auth/profile');
}
/**
 *  Conntected User Information
 */
function patchConnectedUser(user: User): AxiosPromise<User> {
    return axiosInstance.patch('/auth/profile', user);
}

function uploadUserPhoto(photoData: any): AxiosPromise<User> {
    return axiosInstance.patch('/auth/profile/photo', photoData);
}

/**
 * Factures - Bulk export invoices as ZIP
 */
function getBulkInvoiceExport(invoiceIds: number[]): AxiosPromise<any> {
    return axiosInstance.post('/admin/invoices/bulk-export', {
        invoice_ids: invoiceIds
    }, {
        responseType: 'blob',
        headers: {
            'Accept': 'application/zip'
        }
    });
}

export function getBulkInvoiceExportCsv(invoiceIds: number[]): AxiosPromise<Blob> {
  return axiosInstance.post(
    '/admin/invoices/bulk-export-csv',
    { invoice_ids: invoiceIds },
    {
      responseType: 'blob',
      headers: { Accept: 'text/csv' }
    }
  );
}


/**
 * Inscription
 */

interface RegisterRequest {
    civility: 'Madame' | 'Monsieur';
    firstname: string;
    lastname: string;
    company: string;
    email: string;
    password: string;
}

interface RegisterResponse {
    token: string;
}

function postRegisterPrescriber(request: RegisterRequest) {
    return axios.post<RegisterResponse>(
        `${BACKEND_URL}/api/public/register/prescriber`,
        request
    );
}

function getProgramsCount() {
    return axios.get(
        `${BACKEND_URL}/api/public/programsCount`
    );
}

/**
 * Réinitialisation du mot de passe
 */
function requestPassword(email: string, isAdmin: boolean = false) {
    return axios.patch(`${BACKEND_URL}/api/public/password/request`, { email, isAdmin });
}

interface PlainPassword {
    first: string;
    second: string;
}

function resetPassword(token: string, plainPassword: PlainPassword) {
    return axios.patch(`${BACKEND_URL}/api/public/password/reset/${token}`, {
        plainPassword,
    });
}
/**
 * Réinitialisation du mot de passe - Demande de support
 */
function requestSupportPasswordReset(email: string) {
    return axios.patch(`${BACKEND_URL}/api/public/password/support-request`, { email });
}


function postForceResetPassword(payload: { email: string; adminEmail: string }) {
    return axios.patch(`${BACKEND_URL}/api/public/password/force-request`, payload);
  }


interface DocumentPrescriberRequest {
    file_name: string;
    document: string;
}

function postPrescriberDocumentAdmin(
    userID: number,
    type: DOCUMENT_PRESCRIBER_TYPE,
    req: DocumentPrescriberRequest
) {
    return axiosInstance.post(`/admin/users/${userID}/documents/${type}`, req);
}

function postPrescriberSubscriptionCancel(
    userID: number,
) {
    return axiosInstance.get(`/admin/subscriptions/${userID}/delete_sub`);
}

function postSubscriptionCreate(
    userID: number,
    jc: boolean
) {
    return axiosInstance.post(`/admin/subscriptions/create/${userID}/${jc}`);
}


// ******************************************* Trial Chapter *******************************************

// Get all trial steps and chapters
 function getTrialSteps() {
    return axiosInstance.get('/admin/trial-steps');
}

 function postTrialChapter(payload: any) {
    // Send as JSON (no specific Content-Type header needed, axios handles it)
    return axiosInstance.post('/admin/trial-steps/chapter', payload);
}

 function putTrialChapter(id: number, payload: any) {
    return axiosInstance.post(`/admin/trial-steps/chapter/${id}`, payload);
}

 function deleteTrialChapter(id: number) {
    return axiosInstance.delete(`/admin/trial-steps/chapter/${id}`);
}


function getTrialProgress() {
  return axiosInstance.get('/trial/progress');
}


function postTrialProgress(payload: {
  chapterId: number;
  watchedSeconds: number;
  lastPosition: number;
  isCompleted: boolean;
}) {
  return axiosInstance.post('/trial/progress', payload);
}



function getUserTrialProgressAdmin(userId: number) {
  return axiosInstance.get(`/admin/users/${userId}/trial-progress`);
}


function getUserPasswordResetAdmin(userId: number) {
  return axiosInstance.get(`/admin/users/${userId}/password-reset`);
}

function putUserPasswordResetAdmin(
  userId: number,
  data: { enabled?: boolean; clear_request?: boolean }
) {
  return axiosInstance.put(`/admin/users/${userId}/password-reset`, data);
}


function postUsersTrialStats(userIds: number[]) {
    return axiosInstance.post('/admin/users/trial-stats', { userIds });
}



// ******************************************* Trial Chapter *******************************************



// Get all webinars for a user
function getWebinars(userID: number) {
    return axiosInstance.get(`/admin/webinar/user/${userID}`);
}

// Create a new webinar
function postWebinar(
    userID: number,
    data: {
        title: string;
        link: string;
    }
) {
    return axiosInstance.post(`/admin/webinar/user/${userID}`, data);
}

// Update an existing webinar
function putWebinar(
    webinarID: number,
    data: {
        title: string;
        link: string;
    }
) {
    return axiosInstance.put(`/admin/webinar/${webinarID}`, data);
}

// Delete a webinar
function deleteWebinar(webinarID: number) {
    return axiosInstance.delete(`/admin/webinar/${webinarID}`);
}

// Bulk create webinars (optional - for multiple webinars at once)
function postWebinarsBulk(
    userID: number,
    webinars: Array<{
        title: string;
        link: string;
    }>
) {
    return axiosInstance.post(`/admin/webinar/bulk/user/${userID}`, {
        webinars: webinars
    });
}





        // NEW Access 
        // payload complet pour création/MAJ
        function postSubscriptionForm(
                    userID: number,
                    data: {
                    is_trial_active: boolean;
                    is_subscription_active: boolean;
                    is_jc: boolean;
                    with_hono: boolean;
                    commission_rate: number;
                    }
                ) {
                    return axiosInstance.post(
                    `/admin/subscriptions/form/${userID}`,
                    data
                    );
                }


                // mise à jour
         function putSubscriptionForm(
                userID: number,
                subscriptionID: number,
                data: {
                is_trial_active: boolean;
                is_subscription_active: boolean;
                is_jc: boolean;
                with_hono: boolean;
                commission_rate: number;
                }
            ) {
                return axiosInstance.put(
                `/admin/subscriptions/form/${userID}/${subscriptionID}`,
                data
                );
            }

  



function getPrescriberDocumentAdmin(
    userID: number,
    type: DOCUMENT_PRESCRIBER_TYPE
) {
    return axiosInstance.get(`/admin/users/${userID}/documents/${type}`, {
        responseType: 'blob',
    });
}

/**
 * Users - Get all users for datatable
 */
function getUsers(meta: MetaDatatable | null = null) {
    return axiosInstance.get('/admin/users', {
        params: {
            meta,
        },
    });
}

/**
 * Users - Updt trial end
 */
function getUserNewTrial(userId: number, nbOfDays: number) {
    return axiosInstance.get(`/admin/users/${userId}/trial_add/${nbOfDays}`);
}

function getArticles(meta: MetaDatatable | null = null) {
    return axiosInstance.get(`/admin/articles`, {
        params: {
            meta,
        },
    });
}

function getArticle(id: number): AxiosPromise<Lot> {
    return axiosInstance.get(`/admin/articles/${id}`);
}

function postArticle(article: any): AxiosPromise<Lot> {
    return axiosInstance.post('/admin/articles', article);
}

function patchArticle(article: any, id: number): AxiosPromise<Lot> {
    return axiosInstance.patch(`/admin/articles/${id}`, article);
}


/**
 * Options - Delete option
 */
function deleteArticle(id: number): AxiosPromise<OptionModel> {
    return axiosInstance.delete(`/admin/articles/${id}`);
}

function getAutomations(meta: MetaDatatable | null = null) {
    return axiosInstance.get(`/admin/automations`, {
        params: {
            meta,
        },
    });
}

function deleteAutomation(id: number): AxiosPromise<OptionModel> {
    return axiosInstance.delete(`/admin/automation/${id}`);
}

function deleteAutomationArticle(id: number, articleId: number): AxiosPromise<OptionModel> {
    return axiosInstance.delete(`/admin/automation/${id}/${articleId}`);
}

function postAutomation(automation: any): AxiosPromise<Lot> {
    return axiosInstance.post<Lot>('/admin/automation', automation);
}

function generateAutomationsReport(): AxiosPromise {
    return axiosInstance.get('/admin/automation/export', {
        responseType: 'blob',
    });
}

/**
 * Users - Updt trial end
 */
function getContracts() {
    return axiosInstance.get(`/admin/contracts`);
}

/**
 * Users - Updt trial end
 */
function getContract(id: string) {
    return axiosInstance.get(`/admin/contracts/${id}`);
}

/**
 * Users - Updt trial end
 */
function generateContract(contractId: string, userId: string) {
    return axiosInstance.get(`/admin/contract-generate/${contractId}/${userId}`, {
        responseType: 'blob'
    });
}



/**
 * Users - Updt trial end
 */
function addTypeContract(type: string) {
    return axiosInstance.post(`/admin/contracts/add-type`, { type });
}

function getContractType(id: any) {
    return axiosInstance.get(`admin/contract-type/${id}`)
}

function addContract(contract: any, type: any) {
    return axiosInstance.post(`admin/contracts/add`, {
        contract,
        type
    })
}

function editContract(contract: any, id: any) {
    return axiosInstance.post(`admin/contracts/edit/${id}`, {
        contract
    })
}

/**
 * Users - Post user
 */
function postUser(user: User): AxiosPromise<User> {
    return axiosInstance.post<User>('/admin/users', user);
}

/**
 *  Users - Get User
 */
function getUser(id: number): AxiosPromise<User> {
    return axiosInstance.get<User>(`/admin/users/${id}`);
}
/**
 *
 *  Users - Patch User
 */
function patchUser(id: number, user: User): AxiosPromise<User> {
    return axiosInstance.patch(`/admin/users/${id}`, user);
}

/**
 * Users - Delete User
 */
function deleteUser(id: number): AxiosPromise<User> {
    return axiosInstance.delete(`/admin/users/${id}`);
}

/**
 * Users - Get Business Developpers
 */
function getBusinessDevelopers(): AxiosPromise<User[]> {
    return axiosInstance.get('/admin/users/business_developpers')
}

/**
 * Companies - Get all companies for datatable
 */
function getCompanies(meta: MetaDatatable | null = null) {
    return axiosInstance.get('/admin/companies', {
        params: {
            meta,
        },
    });
}

/**
 * Companies - Post company
 */
function postCompany(company: Company): AxiosPromise<Company> {
    return axiosInstance.post<Company>('/admin/companies', company);
}

/**
 *  Companies - Get company
 */
function getCompany(id: number): AxiosPromise<Company> {
    return axiosInstance.get<Company>(`/admin/companies/${id}`);
}
/** Companies - Get Mandat **/
function getCompanyMandat(id: number): AxiosPromise<string> {

    return axiosInstance.get(`/admin/companies/${id}/mandat`, { responseType: 'arraybuffer' });
}
/** get all companies of prescribers **/
function getPrescribersCompanies() {
    return axiosInstance.get('/admin/users/companies_prescriber');
}

/**
 *
 *  Companies - Patch company
 */
function patchCompany(id: number, company: Company): AxiosPromise<Company> {
    return axiosInstance.patch(`/admin/companies/${id}`, company);
}

/**
 * Companies - Delete company
 */
function deleteCompany(id: number): AxiosPromise<Company> {
    return axiosInstance.delete(`/admin/companies/${id}`);
}

/**
 * Companies - Patch company contacted_at
 */
function patchCompanyContactedAt(id: number): AxiosPromise<Company> {
    return axiosInstance.patch(`/admin/companies/${id}/contacted_at`);
}



/**
 * Customers - Get all customers for datatable
 */
function getCustomers(meta: MetaDatatable | null = null) {
    return axiosInstance.get('/admin/customers', {
        params: {
            meta,
        },
    });
}

/**
 * Customers - Post customer
 */
function postCustomer(customer: Customer): AxiosPromise<Customer> {
    return axiosInstance.post<Customer>('/admin/customers', customer);
}

/**
 *  Customers - Get customer
 */
function getCustomer(id: number): AxiosPromise<Customer> {
    return axiosInstance.get<Customer>(`/admin/customers/${id}`);
}
/**
 *
 *  Customers - Patch customer
 */
function patchCustomer(id: number, customer: Customer): AxiosPromise<Customer> {
    return axiosInstance.patch(`/admin/customers/${id}`, customer);
}

/**
 * Customers - Delete customer
 */
function deleteCustomer(id: number): AxiosPromise<Customer> {
    return axiosInstance.delete(`/admin/customers/${id}`);
}

/**
 * Programms - Get all programs for datatable
 */
function getPrograms(meta: MetaDatatable | null = null) {
    return axiosInstance.get('/admin/programs', {
        params: {
            meta,
        },
    });
}

/**
 * Programms - Post program
 */
function postProgram(program: Program): AxiosPromise<Program> {
    return axiosInstance.post<Program>('/admin/programs', program);
}

/**
 *  Programms - Get program
 */
function getProgram(id: number): AxiosPromise<Program> {
    return axiosInstance.get<Program>(`/admin/programs/${id}`);
}

/** Programms - Get Mandat **/
function getProgramMandat(id: number): AxiosPromise<string> {

    return axiosInstance.get(`/admin/program/${id}/mandat`, { responseType: 'arraybuffer' });
}
/**
 *  Programms - Patch program
 */
function patchProgram(id: number, program: Program): AxiosPromise<Program> {
    return axiosInstance.patch(`/admin/programs/${id}`, program);
}

/**
 * Programms - Delete program
 */
function deleteProgram(id: number): AxiosPromise<Program> {
    return axiosInstance.delete(`/admin/programs/${id}`);
}

/**
 *  Programs - Push Images
 */
function pushImagesProgram(
    id: number,
    images: any[],
    type: 'images_secondary_perspective'
) {
    return axiosInstance.post(`/admin/programs/${id}/images/${type}`, {
        images,
    });
}

/**
 *  Programs - Push Documents
 */
function pushDocumentsProgram(
    id: number,
    documents: ProgramDocumentRequest[],
    type: DOCUMENT_TYPE
) {
    return axiosInstance.post(`/admin/programs/${id}/documents/${type}`, {
        documents,
    });
}

/**
 *  Programs - delete Image
 */
function deleteImageProgram(programID: number, imageID: number) {
    return axiosInstance.delete(
        `/admin/programs/${programID}/images/${imageID}`
    );
}

/**
 *  Programs - delete broken Image (prescriber)
 */
function deleteImageProgramPrescriber(programID: number, imageID: number) {
    return axiosInstance.delete(
        `/prescriber/programs/${programID}/images/${imageID}`
    );
}

/**
 *  Programs - delete Image
 */
function deleteDocumentProgram(programID: number, documentID: number) {
    return axiosInstance.delete(
        `/admin/programs/${programID}/documents/${documentID}`
    );
}

/**
 *  Programs - update stocks
 */
function patchUpdateProgramStock(programID: number) {
    return axiosInstance.patch(
        `/admin/programs/${programID}/stock_updated_at`
    );
}

/**
 * Lots - Get all lots for datatable
 */
function getLots(programId: number, meta: MetaDatatable | null = null) {
    return axiosInstance.get(`/admin/programs/${programId}/lots`, {
        params: {
            meta,
        },
    });
}

function getProgramArchiveAdmin(programID: number) {
    return axiosInstance.get(`/admin/programs/${programID}/archive`, {
        responseType: 'blob',
    });
}

function getProgramArchiveByTypeAdmin(programID: number, type: string) {
    return axiosInstance.get(`/admin/programs/${programID}/archive/${type}`, {
        responseType: 'blob',
    });
}

/**
 *  Lots - Get lot
 */
function getLot(id: number): AxiosPromise<Lot> {
    return axiosInstance.get<Lot>(`/admin/lots/${id}`);
}

/**
 * Lots - Post lot
 */
function postLot(lot: Lot): AxiosPromise<Lot> {
    return axiosInstance.post<Lot>('/admin/lots', lot);
}

/**
 *
 *  Lots - Patch lot
 */
function patchLot(id: number, lot: Lot): AxiosPromise<Lot> {
    return axiosInstance.patch(`/admin/lots/${id}`, lot);
}

/**
 * Lots - Delete lot
 */
function deleteLot(id: number): AxiosPromise<Lot> {
    return axiosInstance.delete(`/admin/lots/${id}`);
}

/**
 *
 *  Lots - Patch lot option
 */
function patchLotOptionEndAt(id: number | undefined, option_end_at: string): AxiosPromise<Lot> {
    return axiosInstance.patch(`/admin/lots/${id}/option_update_end_at`, { 'option_end_at': option_end_at });
}

function importLotCSV(id: number, csv: File): AxiosPromise<any> {
    const formData = new FormData();
    formData.append('csv', csv);
    return axiosInstance.post<ImportCSVResult>(
        `/admin/lots/import/${id}`,
        formData,
        {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        }
    );
}

function importPlanZip(id: number, zip: File): AxiosPromise<any> {
    const formData = new FormData();
    formData.append('zip', zip);
    return axiosInstance.post<ImportZIPResult>(
        `/admin/lots/plans/import/${id}`,
        formData,
        {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        }
    );
}

/**
 * Dénonciations - Get all denounces for datatable
 */
function getDenounces(meta: MetaDatatable | null = null) {
    return axiosInstance.get('/admin/denounces', {
        params: {
            meta,
        },
    });
}

/**
 *  Dénonciations - Get denounce
 */
function getDenounce(id: number): AxiosPromise<Denounce> {
    return axiosInstance.get<Denounce>(`/admin/denounces/${id}`);
}

function getNumberDenouncesPending() {
    return axiosInstance.get<number>(`/admin/denounces/pending`);
}

function getLastPendingDenounce(idPrescriber: number) {
    return axiosInstance.get(`/prescriber/denounces/${idPrescriber}/pending/last`);
}

function deleteDenouncePrescriberById(denounceId: number): AxiosPromise<any> {
    return axiosInstance.delete(`/prescriber/denounces/${denounceId}`);
}

function getDenouncePrescriberStats(): AxiosPromise<{ total: number; accepted: number }> {
    return axiosInstance.get('/prescriber/denounces/stats');
}

/**
 * Dénonciations - Post denounce
 */
function postDenounce(denounce: Denounce): AxiosPromise<Denounce> {
    return axiosInstance.post<Denounce>('/admin/denounces', denounce);
}

/**
 *
 *  Dénonciations - Patch denounce
 */
function patchDenounce(id: number, denounce: Denounce): AxiosPromise<Denounce> {
    return axiosInstance.patch(`/admin/denounces/${id}`, denounce);
}

/**
 * Dénonciations - Delete denounce
 */
function deleteDenounce(id: number): AxiosPromise<Denounce> {
    return axiosInstance.delete(`/admin/denounces/${id}`);
}

/**
 * Options - Get all options for datatable
 */
function getOptions(meta: MetaDatatable | null = null) {
    return axiosInstance.get('/admin/options', {
        params: {
            meta,
        },
    });
}

/**
 *  Options - Get option
 */
function getOption(id: number): AxiosPromise<OptionModel> {
    return axiosInstance.get<OptionModel>(`/admin/options/${id}`);
}
function getNumberOptionsPending() {
    return axiosInstance.get<number>(`/admin/options/pending`);
}

/**
 * Options - Post option
 */
function postOption(option: OptionModel): AxiosPromise<OptionModel> {
    return axiosInstance.post<OptionModel>('/admin/options', option);
}

/**
 *
 *  Options - Patch option
 */
function patchOption(
    id: number,
    option: OptionModel
): AxiosPromise<OptionModel> {
    return axiosInstance.patch(`/admin/options/${id}`, option);
}

/**
 * Options - Delete option
 */
function deleteOption(id: number): AxiosPromise<OptionModel> {
    return axiosInstance.delete(`/admin/options/${id}`);
}

/**
 * Booking - Get all bookins for datatable
 */

function getBookingOperators(search: string = '') {
    return axiosInstance.get('/admin/booking-operators', {
        params: {
            search: search
        }
    });
}

function getOptionOperators(search: string = '') {
    return axiosInstance.get('/admin/option-operators', {
        params: {
            search: search
        }
    });
}

function getDenounceOperators(search: string = '') {
    return axiosInstance.get('/admin/denounce-operators', {
        params: {
            search: search
        }
    });
}




function getBookings(meta: MetaDatatable | null = null) {
    return axiosInstance.get('/admin/bookings', {
        params: {
            meta,
        },
    });
}

/**
 *  Booking - Get booking
 */
function getBooking(id: number): AxiosPromise<Booking> {
    return axiosInstance.get<Booking>(`/admin/bookings/${id}`);
}

function getNumberBookingsPending() {
    return axiosInstance.get<number>(`/admin/bookings/pending`);
}

/**
 * Booking - Post booking
 */
function postBooking(booking: Booking): AxiosPromise<Booking> {
    return axiosInstance.post<Booking>('/admin/bookings', booking);
}

/**
 * Booking - Post booking
 */
function sendBookingSms(booking: Booking) {
    return axiosInstance.get(`/admin/bookings/sms/${booking.id}`);
}

/**
 *
 *  Booking - Patch booking
 */
function patchBooking(id: number, booking: Booking): AxiosPromise<Booking> {
    return axiosInstance.patch(`/admin/bookings/${id}`, booking);
}

/**
 *
 *  Booking - Patch booking backoffice comment (dedicated endpoint : ne touche pas au statut/progression)
 */
function patchBookingBackofficeComment(
    id: number,
    backoffice_comment: string
): AxiosPromise<Booking> {
    return axiosInstance.patch(`/admin/bookings/${id}/backoffice-comment`, {
        backoffice_comment,
    });
}

/**
 * Booking - Delete booking
 */
function deleteBooking(id: number, type: string): AxiosPromise<Booking> {
    return axiosInstance.delete(`/admin/bookings/${id}/${type}`);
}

function postBookingDocumentAdmin(
    bookingID: number,
    type: string,
    req: DocumentBookingRequest
) {
    return axiosInstance.post(
        `/admin/bookings/${bookingID}/documents/${type}`,
        req
    );
}

function deleteBookingDocumentAdmin(
    bookingID: number,
    type: string,
    comment: string
) {
    return axiosInstance.delete(
        `/admin/bookings/${bookingID}/documents/${type}-${comment}`);
}

function getBookingDocumentAdmin(bookingID: number, type: string) {
    return axiosInstance.get(`/admin/bookings/${bookingID}/documents/${type}`, {
        responseType: 'blob',
    });
}

function postBookingContractReceived(bookingID: number) {
    return axiosInstance.post(`/admin/bookings/${bookingID}/contract_received`);
}

function patchBookingRegistration(
    bookingID: number,
    req: PatchBookingRegistrationRequest
) {
    return axiosInstance.patch(
        `/admin/bookings/${bookingID}/registration`,
        req
    );
}

function postBookingGoToFunding(bookingID: number) {
    return axiosInstance.post(`/admin/bookings/${bookingID}/go_to_funding`);
}

function postBookingPropertyAttestationReceived(bookingID: number) {
    return axiosInstance.post(
        `/admin/bookings/${bookingID}/property_attestation_received`
    );
}

function postBookingInvoiceDocumentReceived(bookingID: number) {
    return axiosInstance.post(
        `/admin/bookings/${bookingID}/invoice_document_received`
    );
}

/**
 * Notifications - Get all notifications for datatable
 */
function getNotifications(meta: MetaDatatable | null = null) {
    return axiosInstance.get('/admin/notifications', {
        params: {
            meta,
        },
    });
}

/**
 *  Notifications - Get notification
 */
function getNotification(id: number): AxiosPromise<NotificationModel> {
    return axiosInstance.get<NotificationModel>(`/admin/notifications/${id}`);
}

/**
 *  Notifications - Get notification
 */
function getKochise(req: Object) {
    return axiosInstance.get(`/prescriber/kochise`, {
        params: {
            req
        },
    });
}

/**
 * Notifications - Post notification
 */
function postNotification(
    notification: NotificationModel
): AxiosPromise<NotificationModel> {
    return axiosInstance.post<NotificationModel>(
        '/admin/notifications',
        notification
    );
}

/**
 *
 *  Notifications - Patch booking
 */
function patchNotification(
    id: number,
    notification: NotificationModel
): AxiosPromise<NotificationModel> {
    return axiosInstance.patch(`/admin/notifications/${id}`, notification);
}

/**
 * Notifications - Delete booking
 */
function deleteNotification(id: number): AxiosPromise<NotificationModel> {
    return axiosInstance.delete(`/admin/notifications/${id}`);
}

/**
 * Factures - Get all invoices for datatable
 */
function getInvoices(meta: MetaDatatable | null = null) {
    return axiosInstance.get('/admin/invoices', {
        params: {
            meta,
        },
    });
}

/**
 * Factures - post invoice
 */
function postInvoice(data: any | null = null) {
    return axiosInstance.post('/admin/invoices', data);
}

/**
 * Factures - post invoice
 */
function postCancelInvoice(invoice: Invoice) {
    return axiosInstance.post(`/admin/invoices/cancel/${invoice.id}`);
}

/**
 *  Factures - Get invoice
 */
function getInvoice(id: number): AxiosPromise<Invoice> {
    return axiosInstance.get<Invoice>(`/admin/invoices/${id}`);
}

/**
 *  Factures - Send email
 */
function getInvoiceSendEmail(id: number): AxiosPromise<Invoice> {
    return axiosInstance.get<Invoice>(`/admin/invoices/email/${id}`);
}

/**
 * Prescripteur Factures - Get all invoices for current prescriber
 */
function getInvoicesPrescriber() {
    return axiosInstance.get('/prescriber/invoices');
}

/**
 * Prescripteur Multi Licence
 */
function getRequestMultiLicence() {
    return axiosInstance.get('/prescriber/multiLicence');
}

/**
 *  Factures - Download pdf
 */
function getInvoiceDownloadPdfPrescriber(id: number): AxiosPromise<Invoice> {
    return axiosInstance.get<Invoice>(`/prescriber/invoices/pdf/${id}`, {
        responseType: 'blob'
    });
}

/**
 *  Factures - Download pdf
 */
function getInvoiceDownloadPdf(id: number): AxiosPromise<Invoice> {
    return axiosInstance.get<Invoice>(`/admin/invoices/pdf/${id}`, {
        responseType: 'blob'
    });
}

/**
 * Paiements - Get all payments for datatable
 */
function getPayments(meta: MetaDatatable | null = null) {
    return axiosInstance.get('/admin/payments', {
        params: {
            meta,
        },
    });
}

/**
 *  Paiements - Get payment
 */
function getPayment(id: number): AxiosPromise<Payment> {
    return axiosInstance.get<Payment>(`/admin/payments/${id}`);
}

/**
 * Paiements - Post payment
 */
function postPayment(payment: Payment): AxiosPromise<Payment> {
    return axiosInstance.post<Payment>('/admin/payments', payment);
}

/**
 *
 *  Paiements - Patch payment
 */
function patchPayment(id: number, payment: Payment): AxiosPromise<Payment> {
    return axiosInstance.patch(`/admin/payments/${id}`, payment);
}

/**
 * Paiements - Delete payment
 */
function deletePayment(id: number): AxiosPromise<Payment> {
    return axiosInstance.delete(`/admin/notifications/${id}`);
}

/**
 * Abonnement - Get all subscriptions for datatable
 */
function getSubscriptions(meta: MetaDatatable | null = null) {
    return axiosInstance.get('/admin/subscriptions', {
        params: {
            meta,
        },
    });
}

/**
 *  Abonnement - Get subscription
 */
function getSubscription(id: number): AxiosPromise<Subscription> {
    return axiosInstance.get<Subscription>(`/admin/subscriptions/${id}`);
}

/**
 *  Send Mail Update requested
 */
function getRequestUpdate(id: Number) {
    return axiosInstance.get(`/prescriber/program/${id}/request_update`);
}

/**
 *
 *  Abonnement - Patch subscription
 */
function patchSubscription(
    id: number,
    subscription: Subscription
): AxiosPromise<Subscription> {
    return axiosInstance.patch(`/admin/subscriptions/${id}`, subscription);
}

/**
 * UserToken - liste des tokens pour un utilisateurs
 */
function getAdminUsertokens(id?: number): AxiosPromise<UserToken> {
    return axiosInstance.get(`/admin/userTokens/${id}`);
}

/**
 * UserToken - creation des identifiants au plugin Carte
 */
function postAdminUserTokenGenerate(user: User): AxiosPromise<UserToken> {
    return axiosInstance.post(`/admin/userToken/create`, user);
}

/**
 * UserToken - creation des identifiants au plugin Carte
 */
function patchAdminUserTokenRegenerate(id?: number): AxiosPromise<UserToken> {
    return axiosInstance.patch(`/admin/userToken/${id}/regenerate`);
}

/**
 * UserToken -  changement des Ips autorisé sur un userToken
 */
function patchAdminUserTokenIp(id?: number, ips?: string): AxiosPromise<UserToken> {
    return axiosInstance.patch(`/admin/userToken/${id}/ip`,
        { 'ips': ips }
    );
}

/**
 * UserToken - désactive un userToken
 */
function patchAdminUserTokenUnactive(id?: number): AxiosPromise<UserToken> {
    return axiosInstance.patch(`/admin/userToken/${id}/unactive`);
}

/**
 * UserToken - Réactive un userToken
 */
function patchAdminUserTokenReactive(id?: number): AxiosPromise<UserToken> {
    return axiosInstance.patch(`/admin/userToken/${id}/reactive`);
}


/**
 * Customers - Get all customers for datatable
 */
function getCustomersPrescriber(meta: MetaDatatable | null = null) {
    return axiosInstance.get('/prescriber/customers', {
        params: {
            meta,
        },
    });
}

/**
 *  Customers - Get customer
 */
function getCustomerPrescriber(id: number): AxiosPromise<Customer> {
    return axiosInstance.get<Customer>(`/prescriber/customers/${id}`);
}

/**
 * Customers - Post customer
 */
function postCustomerPrescriber(customer: Customer): AxiosPromise<Customer> {
    return axiosInstance.post<Customer>('/prescriber/customers', customer);
}

/**
 * Customers - Post customer
 */
function postSponsorship(entity: Sponsorship) {
    return axiosInstance.post('/prescriber/sponsorship/add', entity);
}

function postDenouncePrescriber(request: DenounceRequest) {
    return axiosInstance.post(`/prescriber/denounces`, request);
}


/**
 *
---------------
to implement  !!!
 ---------------
 */
// function postDenounceFile(file: FormData) {
//     return axiosInstance.post(`/prescriber/denounces`, file);

// }

/**
 *
 *  Customers - Patch customer
 */
function patchCustomerPrescriber(
    id: number,
    customer: Customer
): AxiosPromise<Customer> {
    return axiosInstance.patch(`/prescriber/customers/${id}`, customer);
}

/**
 * Customers - Delete customer
 */
function deleteCustomerPrescriber(id: number): AxiosPromise<Customer> {
    return axiosInstance.delete(`/prescriber/customers/${id}`);
}

/**
 * Booking - Get all bookins for datatable
 */
function getBookingsPrescriber(meta: MetaDatatable | null = null) {
    return axiosInstance.get('/prescriber/bookings', {
        params: {
            meta,
        },
    });
}

/**
 * Booking - Stream bookings as NDJSON for the prescriber datatable
 */
function getBookingsPrescriberStream(meta: MetaDatatable | null = null): Promise<Response> {
    const token = AuthModule.token;
    const protocol = window.location.protocol;
    const base = protocol + BACKEND_URL + '/api/prescriber/bookings';
    const query = meta ? `?meta=${encodeURIComponent(JSON.stringify(meta))}` : '';
    return fetch(base + query, {
        headers: { 'Authorization': `Bearer ${token}` },
    });
}

/**
 *  Booking - Get booking
 */
function getBookingPrescriber(id: number): AxiosPromise<Booking> {
    return axiosInstance.get<Booking>(`/prescriber/bookings/${id}`);
}

/**
 * Booking - Post booking
 */
function postBookingPrescriber(
    lotID: number,
    customerID: number,
    force: boolean = false
  ): AxiosPromise<Booking> {
    // Construire l'URL avec le paramètre force si nécessaire
    const url = force 
      ? `/prescriber/bookings?force=true` 
      : `/prescriber/bookings`;
      
    return axiosInstance.post<Booking>(url, {
      lot: lotID,
      customer: customerID,
    });
  }

/**
 *
 *  Booking - Patch booking
 */
function patchBookingPrescriber(
    id: number,
    booking: Booking
): AxiosPromise<Booking> {
    return axiosInstance.patch(`/prescriber/bookings/${id}`, booking);
}

/**
 * Booking - Delete booking
 */
function deleteBookingPrescriber(id: number): AxiosPromise<Booking> {
    return axiosInstance.delete(`/prescriber/bookings/${id}`);
}

/**
 * Notifications - Get all notifications for datatable
 */
function getNotificationsPrescriber() {
    return axiosInstance.get('/prescriber/notifications');
}

interface DocumentBookingRequest {
    file_name: string;
    document: string;
}

function postBookingDocumentPrescriber(
    bookingID: number,
    type: string,
    req: DocumentBookingRequest
) {
    return axiosInstance.post(
        `/prescriber/bookings/${bookingID}/documents/${type}`,
        req
    );
}

function postCustomerDocumentPrescriber(
    customerId: number,
    req: DocumentBookingRequest
) {
    return axiosInstance.post(
        `/prescriber/customers/${customerId}/documents`,
        req
    );
}

function archiveCustomerPrescriber(customerId: number) {
    return axiosInstance.delete(`/prescriber/customers/${customerId}/archive`);
}

function getCustomerDocumentPrescriber(
    customerId: number
) {
    return axiosInstance.get(
        `/prescriber/customers/${customerId}/documents`);
}

function deleteCustomerDocumentPrescriber(
    customerId: number,
    docId: number
) {
    return axiosInstance.get(
        `/prescriber/customers/${customerId}/documents/delete/${docId}`);
}

function getCustomerDocumentPrescriberById(
    customerId: number,
    docId: number
) {
    return axiosInstance.get(
        `/prescriber/customers/${customerId}/documents/${docId}`, {
        responseType: 'blob'
    });
}

function getCheckoutSession(
    offer: Offer,
) {
    return axiosInstance.get(
        `/prescriber/payment/checkout-session/${offer.id}`
    );
}

function getCheckoutSessionData(
    price: number,
    nbData: number
) {
    return axiosInstance.get(
        `/prescriber/payment/checkout-session-data/${price}-${nbData}`
    );
}

function getVerificationPayment() {
    return axiosInstance.get(
        `/prescriber/payment/verif_checkout`
    );
}

function deleteBookingDocumentPrescriber(
    bookingID: number,
    type: string,
) {
    return axiosInstance.delete(
        `/prescriber/bookings/${bookingID}/documents/${type}`
    );
}


function getBookingDocumentPrescriber(bookingID: number, type: string) {
    return axiosInstance.get(
        `/prescriber/bookings/${bookingID}/documents/${type}`,
        {
            responseType: 'blob',
        }
    );
}

interface BookingDateSignatureAppointmentPrescriberRequest {
    dateSignatureAppointment: Date;
}

function postBookingDateSignatureAppointmentPrescriber(
    bookingID: number,
    req: BookingDateSignatureAppointmentPrescriberRequest
) {
    return axiosInstance.post(
        `/prescriber/bookings/${bookingID}/date_signature_appointment`,
        req
    );
}

function postBookingSendContractMailPrescriber(bookingID: number) {
    return axiosInstance.post(
        `/prescriber/bookings/${bookingID}/send_contract_mail`
    );
}

function postBookingSendContractMailPrescriberOtherAddress(address: string, bookingID: number) {
    return axiosInstance.post(
        `/prescriber/bookings/${address}/${bookingID}/send_contract_mail_other_address`
    );
}

function postBookingContractSendPrescriber(bookingID: number) {
    return axiosInstance.post(
        `/prescriber/bookings/${bookingID}/contract_send`
    );
}

/**
 * Prescripteur - Offres
 */
function getOffersPresciber() {
    return axiosInstance.get<Offer[]>(`/prescriber/offers`);
}

function getOffersPaymentUrlPresciber(offerID: number) {
    return axiosInstance.get<{ url: string }>(
        `/prescriber/offers/payment/url/${offerID}`
    );
}

/**
 * Prescripteur - Abonnements
 */
function getSubscriptionsPrescriber() {
    return axiosInstance.get<SubscriptionPrescriber[]>(
        `/prescriber/subscriptions`
    );
}

/**
 * Prescripteur - Paiements
 */
function postPaymentPrescriber(request: PaymentPrescriberRequest) {
    return axiosInstance.post(`/prescriber/payments`, request);
}

/**
 * Prescripteur - Paiements
 */
function postSaveSearchPrescriber(req: any) {
    return axiosInstance.post('/prescriber/search/save', req);
}

/**
 * Prescripteur - Post envois de mail avec documents
 */
function postSendMailToProspectWithDocuments(req: any) {
    return axiosInstance.post('/prescriber/sendmail/submit', req);
}
/**
 * Prescripteur - Post envois de mail avec documents
 */
function getIsFile(id: any, type: string) {
    return axiosInstance.get(`/prescriber/sendmail/is_file/${type}/${id}`);
}

/**
 * Prescripteur - Consultation des userTokens api pour la carte
 */
function getUserTokensActivePrescriber(): AxiosPromise<UserToken[]> {
    return axiosInstance.get(`/prescriber/userTokens/active`);
}

/**
 * Prescripteur - Consultation des userTokens api pour la carte
 */
function getUserTokensPrescriber(): AxiosPromise<UserToken[]> {
    return axiosInstance.get(`/prescriber/userTokens`);
}

/**
 * Prescripteur - Consultation des userTokens api pour la carte
 */
function saveUserCrmUrl(url: string): any {
    return axiosInstance.patch(`/prescriber/userCrmUrl`, {url});
}

/**
 * Prescripteur - Consultation d'un userToken api pour la carte
 */
function getUserTokenPrescriber(id: number): AxiosPromise<UserToken> {
    return axiosInstance.get(`/prescriber/userToken/${id}`);
}

/**
 * Prescripteur - regeneration des acces API
 */
function getUserTokenRefreshRegenerate(id: number): AxiosPromise<UserToken> {
    return axiosInstance.patch(`/prescriber/userToken/${id}/regenerate`);
}

/**
 * Prescripteur -  changement des Ips autorisé sur un userToken
 */
function patchUserTokenIp(id?: number, ips?: string): AxiosPromise<UserToken> {
    return axiosInstance.patch(`/prescriber/userToken/${id}/ip`,
        { 'ips': ips }
    );
}

/**
 * Options - Get all options for all clients of a user by ID
 */
function getOptionsPrescriberByUserId(userId: number) {
    return axiosInstance.get(`/prescriber/options/user/${userId}`);
}

/**
 * Options - Get all options for the currently logged-in prescriber (no pagination, no year filter)
 */
function getMyOptionsPrescriber(page: number = 1, limit: number = 10) {
    return axiosInstance.get('/prescriber/my-options', { params: { page, limit } });
}

function getMyOptionsPrescriberStats(): AxiosPromise<{ total: number; pending: number; accepted: number; canceled: number }> {
    return axiosInstance.get('/prescriber/my-options/stats');
}

/**
 * Options - Get all options for datatable
 */
function getOptionsPrescriber(meta: MetaDatatable | null = null) {
    return axiosInstance.get('/prescriber/options', {
        params: {
            meta,
        },
    });
}

/**
 *  Options - Get option
 */
function getOptionPrescriber(id: number): AxiosPromise<OptionModel> {
    return axiosInstance.get<OptionModel>(`/prescriber/options/${id}`);
}

/**
 * Options - Post option
 */
function postOptionPrescriber(
    lotID: number,
    denounceID: number
): AxiosPromise<OptionModel> {
    return axiosInstance.post<OptionModel>('/prescriber/options', {
        lot: lotID,
        denounce: denounceID,
    });
}

/**
 * Options - Post option
 */
function postOptionOnAvailablePrescriber(
    lotID: number,
    denounceID: number
): AxiosPromise<OptionModel> {
    return axiosInstance.post<OptionModel>('/prescriber/options_on_available', {
        lot: lotID,
        denounce: denounceID,
    });
}

/**
 *
 *  Options - Patch option
 */
function patchOptionPrescriber(
    id: number,
    option: OptionModel
): AxiosPromise<OptionModel> {
    return axiosInstance.patch(`/prescriber/options/${id}`, option);
}

/**
 * Options - Delete option
 */
function deleteOptionPrescriber(id: number): AxiosPromise<OptionModel> {
    return axiosInstance.delete(`/prescriber/options/${id}`);
}

/**
 * Companies - Get all companies for datatable
 */
function postCompanyPrescriber(company: Company) {
    return axiosInstance.post('/auth/companies', company);
}

function getCompaniesPrescriber(meta: MetaDatatable | null = null) {
    return axiosInstance.get('/prescriber/companies', {
        params: {
            meta,
        },
    });
}

/**
 * Promoter autocomplete: companies matching `search`, sorted from the most
 * to the least diffused programs.
 */
function searchPromotorsPrescriber(search: string) {
    return axiosInstance.get('/prescriber/companies/search-promotors', {
        params: {
            search,
        },
    });
}
/**
 * Programms - Get all programs for datatable
 */
function getProgramsPrescriber(meta: MetaDatatable | null = null) {
    return axiosInstance.get('/prescriber/programs', {
        params: {
            meta,
        },
    });
}

/**
 * Programms - Get all programs with GPS only
 */
function getProgramsGPSOnlyPrescriber(meta: MetaDatatable) {
    return axiosInstance.get<PageData<Program>>('/prescriber/programsGPSOnly', {
        params: {
            meta,
        },
    });
}

function getProgramArchivePrescriber(programID: number) {
    return axiosInstance.get(`/prescriber/programs/${programID}/archive`, {
        responseType: 'blob',
    });
}

function getProgramArchiveByTypePrescriber(
    programID: number,
    documentType: string
) {
    return axiosInstance.get(
        `/prescriber/programs/${programID}/archive/${documentType}`,
        {
            responseType: 'blob',
        }
    );
}

/**
 *  Programms - Get program
 */
function getProgramPrescriber(id: number) {
    return axiosInstance.get<Program>(`/prescriber/programs/${id}`);
}

/**
 * Generate Program Report PDF
 */
function generateProgramReport(programId: number, config: any) {
    return axiosInstance.post(
        `/prescriber/programs/${programId}/report-data`,
        config
    );
}


/**
 * Programms - Get new Programs
 */
function getNewPrograms(id: number): AxiosPromise<Program> {
    return axiosInstance.get<Program>(`/prescriber/newPrograms/${id}`);
}

/**
 * Lots - Get all lots for datatable
 */
function getLotsPrescriber(
    programId: number,
    meta: MetaDatatable | null = null
) {
    return axiosInstance.get(`/prescriber/programs/${programId}/lots`, {
        params: {
            meta,
        },
    });
}

/**
 * Lots - Get all lots for datatable
 */
function getLotComparison(
    lotsId: any
) {
    return axiosInstance.post(`/prescriber/compare/lots`, lotsId, {
        responseType: 'blob'
    });
}



// api/api.ts
function getTrackingConnections(meta: MetaDatatable | null = null) {
  return axiosInstance.get('/admin/tracking-connections', {
    params: { meta }, // <-- IMPORTANT: wrap in { meta }
  });
}


/**
 * Companies - Get docs comment from first program of the company
 */
function getProgramDocsCommentAdmin(companyId: number): AxiosPromise<{ docs_comment: string }> {
    return axiosInstance.get<{ docs_comment: string }>(`/admin/companies/${companyId}/programs/docs-comment`);
}


/**
 * Update program docs comment
 */
function updateProgramDocsComment(companyId: number, docsComment: string): AxiosPromise<void> {
    return axiosInstance.patch(`/admin/companies/${companyId}/programs/docs-comment`, {
        docs_comment: docsComment
    });
}







function saveTrackingConnection(sessionData: {
    userId: number;
    subscriptionId?: number | null;
    ipAddress: string;
    tabId: string;
    startTime: string;
    endTime: string;
    duration: number;
}) {
    return axiosInstance.post('/prescriber/tracking-connection/save', sessionData);
}


function postCommentProgram(
    req: any,
    programId: number
) {
    return axiosInstance.post(`/prescriber/program/${programId}`, req);
}

function getAllLotsPrescriber(
    limit: number,
    orderBy: string,
    field: string,
    filter: any,
    page: number
) {
    return axiosInstance.post(`/prescriber/lots`, {
        limit: limit,
        orderBy: orderBy,
        field: field,
        filter: filter,
        page: page,
    });
}

function postLotsArchivePrescriber(req: { lots: number[] }) {
    return axiosInstance.post(`/prescriber/lots/archive`, req, {
        responseType: 'blob',
    });
}

function postLotsComparisonPrescriber(req: { lots: number[] }) {
    return axiosInstance.post(`/prescriber/lots/comparison`, req, {
        responseType: 'blob',
    });
}

function postSupportMessage(req: {}) {
    return axiosInstance.post('/prescriber/support/send-message', req)
}

function postFinanceRequest(req: {}) {
    return axiosInstance.post('/prescriber/finance/send-request', req)
}

function postCreditRequest(req: {}) {
    return axiosInstance.post('/prescriber/credit/send-request', req)
}

function postSupportAddDaysMessage() {
    return axiosInstance.post('/prescriber/support/add-days')
}

function postYoungAccess() {
    return axiosInstance.post('/prescriber/support/young-access')
}

function postMultiAccess() {
    return axiosInstance.post('/prescriber/support/multi-access')
}

function postPlugFinanceSouscription() {
    return axiosInstance.post('/prescriber/support/plug-finance-souscription')
}

function postPluginDemand() {
    return axiosInstance.post('/prescriber/support/plugin-demand')
}

function getAutocompletion(address: string) {
    // New endpoint: data.geopf.fr/geocodage/search
    return axios.get("https://data.geopf.fr/geocodage/search?q=" + address + "&autocomplete=1");
}



const putUserTrialComplete = () => axios.put('/api/user/trial/complete');


export {
    postPluginDemand,
    postPlugFinanceSouscription,
    postMultiAccess,
    postCreditRequest,
    postFinanceRequest,
    postCommentProgram,
    postYoungAccess,
    postPrescriberSubscriptionCancel,
    postSupportAddDaysMessage,
    getAutocompletion,
    login,
    getUsers,
    postUser,
    getUser,
    patchUser,
    deleteUser,
    getBusinessDevelopers,
    getCompanies,
    postCompany,
    getCompany,
    getCompanyMandat,
    getPrescribersCompanies,
    patchCompany,
    deleteCompany,
    patchCompanyContactedAt,
    getCustomers,
    postCustomer,
    getCustomer,
    patchCustomer,
    deleteCustomer,
    getNewPrograms,
    getPrograms,
    postProgram,
    getProgram,
    getProgramMandat,
    patchProgram,
    deleteProgram,
    getLots,
    getLot,
    deleteLot,
    postLot,
    patchLot,
    patchLotOptionEndAt,
    getConnectedUser,
    getDenounces,
    getDenounce,
    getLastPendingDenounce,
    postDenounce,
    patchDenounce,
    deleteDenounce,
    getOptions,
    getCheckoutSession,
    getCheckoutSessionData,
    getVerificationPayment,
    getOption,
    patchOption,
    deleteOption,
    postOption,
    getBookings,
    getBooking,
    getNumberBookingsPending,
    postBooking,
    patchBooking,
    patchBookingBackofficeComment,
    deleteBooking,
    getNotifications,
    getNotification,
    postNotification,
    patchNotification,
    deleteNotification,
    getNotificationsPrescriber,
    getInvoices,
    getInvoice,
    getInvoiceSendEmail,
    getRequestUpdate,
    getInvoiceDownloadPdf,
    getInvoicesPrescriber,
    getInvoiceDownloadPdfPrescriber,
    getPayments,
    getPayment,
    postPayment,
    patchPayment,
    deletePayment,
    getSubscriptions,
    getSubscriptionsPrescriber,
    getOffersPresciber,
    getOffersPaymentUrlPresciber,
    postPaymentPrescriber,
    getCustomersPrescriber,
    getCustomerPrescriber,
    postCustomerPrescriber,
    postDenouncePrescriber,
    // postDenounceFile,
    patchCustomerPrescriber,
    deleteCustomerPrescriber,
    archiveCustomerPrescriber,
    getBookingsPrescriber,
    getBookingsPrescriberStream,
    getBookingPrescriber,
    postBookingPrescriber,
    patchBookingPrescriber,
    deleteBookingPrescriber,
    postBookingDocumentPrescriber,
    sendBookingSms,
    deleteBookingDocumentPrescriber,
    getBookingDocumentPrescriber,
    postBookingDocumentAdmin,
    deleteBookingDocumentAdmin,
    getBookingDocumentAdmin,
    postBookingContractReceived,
    patchBookingRegistration,
    postPrescriberDocumentAdmin,
    getPrescriberDocumentAdmin,
    getOptionPrescriber,
    postSaveSearchPrescriber,
    getOptionsPrescriberByUserId,
    getMyOptionsPrescriber,
    getMyOptionsPrescriberStats,
    getOptionsPrescriber,
    postOptionPrescriber,
    patchOptionPrescriber,
    deleteOptionPrescriber,
    patchConnectedUser,
    postCompanyPrescriber,
    getCompaniesPrescriber,
    searchPromotorsPrescriber,
    requestPassword,
    resetPassword,
    requestSupportPasswordReset,
    importLotCSV,
    importPlanZip,
    pushImagesProgram,
    pushDocumentsProgram,
    deleteDocumentProgram,
    deleteImageProgram,
    deleteImageProgramPrescriber,
    patchUpdateProgramStock,
    getProgramsPrescriber,
    getProgramsGPSOnlyPrescriber,
    getProgramArchivePrescriber,
    getProgramArchiveByTypePrescriber,
    getLotsPrescriber,
    getLotComparison,
    getProgramPrescriber,
    getAllLotsPrescriber,
    getNumberDenouncesPending,
    getNumberOptionsPending,
    postRegisterPrescriber,
    getProgramArchiveAdmin,
    getProgramArchiveByTypeAdmin,
    getRequestMultiLicence,
    postBookingGoToFunding,
    postBookingPropertyAttestationReceived,
    postBookingInvoiceDocumentReceived,
    postBookingDateSignatureAppointmentPrescriber,
    postBookingSendContractMailPrescriber,
    postBookingSendContractMailPrescriberOtherAddress,
    postBookingContractSendPrescriber,
    getSubscription,
    postSendMailToProspectWithDocuments,
    getIsFile,
    patchSubscription,
    getAdminUsertokens,
    postAdminUserTokenGenerate,
    patchAdminUserTokenRegenerate,
    patchAdminUserTokenIp,
    patchAdminUserTokenUnactive,
    patchAdminUserTokenReactive,
    postLotsArchivePrescriber,
    postSupportMessage,
    postSponsorship,
    postLotsComparisonPrescriber,
    getProgramsCount,
    getKochise,
    getUserNewTrial,
    postSubscriptionCreate,
    postSubscriptionForm,
    putSubscriptionForm,
    postInvoice,
    postCustomerDocumentPrescriber,
    getCustomerDocumentPrescriber,
    deleteCustomerDocumentPrescriber,
    getCustomerDocumentPrescriberById,
    postCancelInvoice,
    getUserTokensActivePrescriber,
    getUserTokensPrescriber,
    getUserTokenPrescriber,
    getUserTokenRefreshRegenerate,
    patchUserTokenIp,
    getContracts,
    addTypeContract,
    getContractType,
    addContract,
    getContract,
    editContract,
    generateContract,
    getArticles,
    deleteArticle,
    postArticle,
    patchArticle,
    getAutomations,
    deleteAutomation,
    postAutomation,
    getArticle,
    deleteAutomationArticle,
    generateAutomationsReport,
    getBulkInvoiceExport,
    saveUserCrmUrl,
    getWebinars,

    getTrialSteps,
    postTrialChapter,
    putTrialChapter,
    deleteTrialChapter,
    postWebinar,
    putWebinar,
    deleteWebinar,
    getBookingOperators,
    getOptionOperators,
    getDenounceOperators,
    postForceResetPassword,
    saveTrackingConnection,
    getProgramDocsCommentAdmin,
    updateProgramDocsComment,
    uploadUserPhoto,
    generateProgramReport,
    deleteDenouncePrescriberById,
    getDenouncePrescriberStats,
    putUserTrialComplete,

    getTrackingConnections,
    getTrialProgress,
    getUserTrialProgressAdmin,
    getUserPasswordResetAdmin,
    putUserPasswordResetAdmin,
    postUsersTrialStats,
    postTrialProgress

};
